import { Input, Form, Button, notification } from "antd";
import type { FormProps } from "antd";
import { Mail, ArrowRight, Loader2, CheckCircle, ArrowLeft } from "lucide-react";
import { useState } from "react";
import { getUserManagementService } from "../../backend";
import MicroticaLogoColor from "../../assets/logo-color.svg";
import { Link, useSearchParams } from "react-router-dom";
import AuthVisualPanel from "../../components/auth/AuthVisualPanel";

type RequiredMark = FormProps['requiredMark'];

export default function ResetPasswordRequest() {
    const [form] = Form.useForm();
    const [requiredMark] = useState<RequiredMark>('optional');
    const [loading, setLoading] = useState(false);
    const [emailSent, setEmailSent] = useState(false);
    const [searchParams] = useSearchParams();

    async function handleResetPassword(values: { email: string }) {
        try {
            setLoading(true);
            await getUserManagementService().forgotUserPassword({
                username: values.email
            });
            setEmailSent(true);
        } catch (error: any) {
            notification.error({
                message: "Unable to reset password",
                description: error?.response?.data?.message || "Please wait a moment and try again."
            });
        } finally {
            setLoading(false);
        }
    }

    return (
        <div className="login-container">
            {/* Left side - Visual/Brand section */}
            <AuthVisualPanel />

            {/* Right side - Reset password form */}
            <div className="login-form-section">
                <div className="login-form-wrapper">
                    {/* Mobile logo */}
                    <div className="login-mobile-brand">
                        <Link to="/">
                            <img src={MicroticaLogoColor} alt="Microtica" />
                        </Link>
                    </div>

                    {emailSent ? (
                        // Email sent success state
                        <div className="signup-success">
                            <div className="signup-success-icon">
                                <CheckCircle size={48} />
                            </div>
                            <h2 className="signup-success-title">Check your email</h2>
                            <p className="signup-success-text">
                                If an eligible account exists for that address, we'll send a password reset link.
                                Check your inbox and spam folder.
                            </p>
                            <Button 
                                type="default" 
                                size="large"
                                className="signup-success-btn"
                                onClick={() => setEmailSent(false)}
                            >
                                Try a different email
                            </Button>
                            <div className="login-signup-link" style={{ marginTop: 24 }}>
                                Remember your password? <Link to="/auth/login">Sign in</Link>
                            </div>
                        </div>
                    ) : (
                        // Reset password request form
                        <>
                            <div className="login-form-header">
                                <h2 className="login-form-title">Reset your password</h2>
                                <p className="login-form-subtitle">
                                    Enter your email address and we'll send you a link to reset your password.
                                </p>
                            </div>

                            <Form
                                form={form}
                                layout="vertical"
                                requiredMark={requiredMark}
                                onFinish={handleResetPassword}
                                initialValues={{ email: searchParams.get("email") || "" }}
                                className="login-form"
                            >
                                <Form.Item
                                    name="email"
                                    required
                                    rules={[
                                        { required: true, message: `Please enter your email address` },
                                        { pattern: /^\S+@\S+\.\S+$/, message: "Please enter a valid email address" }
                                    ]}
                                >
                                    <Input 
                                        placeholder="Email address" 
                                        prefix={<Mail size={18} strokeWidth={1.5} />}
                                        className="login-input"
                                        size="large"
                                        onChange={() => setEmailSent(false)}
                                    />
                                </Form.Item>

                                <Button 
                                    htmlType="submit" 
                                    type="primary" 
                                    block 
                                    size="large"
                                    disabled={loading}
                                    className={`login-submit-btn ${loading ? 'login-submit-btn-loading' : ''}`}
                                >
                                    {loading ? (
                                        <>
                                            <Loader2 size={18} className="animate-spin" />
                                            <span>Sending...</span>
                                        </>
                                    ) : (
                                        <>
                                            <span>Send reset link</span>
                                            <ArrowRight size={18} className="login-submit-arrow" />
                                        </>
                                    )}
                                </Button>
                            </Form>

                            <div className="login-signup-link">
                                <Link to="/auth/login" className="back-to-login">
                                    <ArrowLeft size={16} />
                                    <span>Back to sign in</span>
                                </Link>
                            </div>
                        </>
                    )}
                </div>

                <div className="login-footer">
                    <span>© {new Date().getFullYear()} Microtica. All rights reserved.</span>
                </div>
            </div>
        </div>
    )
}
